import { ChevronLeftIcon, LayoutDashboardIcon } from 'lucide-react';
import { useState } from 'react';
import { NavLink, useLocation } from 'react-router';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useMe } from '@/platform/auth/use-me';
import { AppLogoLink } from '@/platform/components/app-logo-link';
import { PlatformModuleSwitcher } from '@/platform/components/platform-module-switcher';
import { PlatformSidebarCollapsedProvider } from '@/platform/components/platform-sidebar-context';
import { activeProductCode, productNavRegistration } from '@/platform/lib/product-nav';
import { platformProductTiles, START_PATH } from '@/platform/lib/products';
import { readSidebarCollapsed, writeSidebarCollapsed } from '@/platform/lib/sidebar-collapsed';

function sidebarStorage(): Storage | null {
  return typeof localStorage === 'undefined' ? null : localStorage;
}

/** Desktop module rail: Übersicht, product switch, active product Nav. Collapse is shared across shells. */
export function PlatformSidebar() {
  const me = useMe();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => readSidebarCollapsed(sidebarStorage()));
  const productCode = activeProductCode(location.pathname);
  const registration = productNavRegistration(productCode);
  const ProductNav = registration?.Nav;
  const tiles = me.data ? platformProductTiles(me.data) : [];

  return (
    <aside
      data-collapsed={collapsed ? 'true' : undefined}
      className={cn(
        'relative hidden shrink-0 overflow-hidden border-r border-sidebar-border bg-card contain-layout lg:sticky lg:top-0 lg:flex lg:h-svh lg:flex-col',
        'transition-[width] duration-200 ease-out motion-reduce:transition-none',
        collapsed
          ? 'w-14 [&_.sidebar-copy]:hidden [&_.sidebar-hit]:h-10 [&_.sidebar-hit]:min-h-10 [&_.sidebar-hit]:w-10 [&_.sidebar-hit]:justify-center [&_.sidebar-hit]:gap-0 [&_.sidebar-hit]:px-0 [&_.sidebar-hit_svg]:size-6 [&_.sidebar-mark]:size-6 [&_.sidebar-mark]:text-sm'
          : 'w-64',
      )}
    >
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className={cn(
          'absolute z-10',
          collapsed ? 'top-2.5 left-1/2 -translate-x-1/2' : 'top-2.5 right-1.5',
        )}
        aria-label={collapsed ? 'Navigation ausklappen' : 'Navigation einklappen'}
        aria-expanded={!collapsed}
        onClick={() => {
          setCollapsed((value) => {
            const next = !value;
            writeSidebarCollapsed(sidebarStorage(), next);
            return next;
          });
        }}
      >
        <ChevronLeftIcon
          aria-hidden
          className={cn(
            'size-4 transition-transform duration-200 ease-out motion-reduce:transition-none',
            collapsed && 'rotate-180',
          )}
        />
      </Button>
      <div className="flex h-full w-64 min-w-64 flex-col">
        <div className="flex h-[3.25rem] shrink-0 items-center border-b border-border pr-10">
          <div className={cn('min-w-0 flex-1 px-3', collapsed && 'pointer-events-none invisible')}>
            <AppLogoLink imageClassName="h-8 w-auto" />
          </div>
        </div>

        <PlatformSidebarCollapsedProvider collapsed={collapsed}>
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-x-hidden overflow-y-auto p-2">
            <div className="space-y-0.5">
              <NavLink
                to={START_PATH}
                end
                aria-label="Übersicht"
                title={collapsed ? 'Übersicht' : undefined}
                className={({ isActive }) =>
                  cn(
                    'sidebar-hit flex min-h-9 items-center gap-2 rounded-md px-3 text-sm whitespace-nowrap text-muted-foreground transition-colors hover:bg-accent hover:text-primary',
                    isActive && 'bg-accent font-medium text-primary',
                  )
                }
              >
                <LayoutDashboardIcon className="size-4 shrink-0" aria-hidden />
                <span className="sidebar-copy">Übersicht</span>
              </NavLink>
            </div>

            <PlatformModuleSwitcher tiles={tiles} activeCode={productCode} collapsed={collapsed} />

            {ProductNav ? (
              <div className="min-w-0 border-t border-border pt-3">
                <p className="sidebar-copy px-3 pb-1.5 text-xs font-medium tracking-wide whitespace-nowrap text-muted-foreground uppercase">
                  Navigation
                </p>
                <ProductNav />
              </div>
            ) : null}
          </div>
        </PlatformSidebarCollapsedProvider>
      </div>
    </aside>
  );
}
